// member.* — per-member state changes (ROM toggle, display-name change).
// (protocol-events.md §5)
import { z } from 'zod';
import { MAX_DISPLAY_NAME_LENGTH, MIN_DISPLAY_NAME_LENGTH } from '../constants.js';
import { customCursorSchema } from '../shared/cursor.js';
// ─── ROM mode ──────────────────────────────────────────────────────────────
/** `member.rom` — c→s. */
export const memberRomSchema = z.object({
    t: z.literal('member.rom'),
    rom: z.boolean(),
});
/** `member.rom-changed` — s→c broadcast. */
export const memberRomChangedSchema = z.object({
    t: z.literal('member.rom-changed'),
    uid: z.string(),
    rom: z.boolean(),
    seq: z.number().int().nonnegative(),
    ts: z.number().int().nonnegative(),
});
// ─── name change ───────────────────────────────────────────────────────────
/** `member.name-change` — c→s. Self-rename only. */
export const memberNameChangeSchema = z.object({
    t: z.literal('member.name-change'),
    displayName: z.string().min(MIN_DISPLAY_NAME_LENGTH).max(MAX_DISPLAY_NAME_LENGTH),
});
/** `member.name-changed` — s→c broadcast. */
export const memberNameChangedSchema = z.object({
    t: z.literal('member.name-changed'),
    uid: z.string(),
    displayName: z.string().min(MIN_DISPLAY_NAME_LENGTH).max(MAX_DISPLAY_NAME_LENGTH),
    seq: z.number().int().nonnegative(),
    ts: z.number().int().nonnegative(),
});
// ─── custom cursor (custom-cursor-2026-09-19.md §2.3) ───────────────────────
/**
 * `member.cursor-change` — c→s. Confirms a custom cursor (or clears it with
 * `cursor: null`). Self-only, same shape family as `member.name-change`.
 */
export const memberCursorChangeSchema = z.object({
    t: z.literal('member.cursor-change'),
    cursor: customCursorSchema.nullable(),
});
/**
 * `member.cursor-changed` — s→c broadcast to everyone (originator included,
 * matching `member.name-changed`'s UX-confirmation rationale). Carries a
 * `seq` because it's part of persistent member state (rebuilt via
 * `room.welcome.members[]` / `room.resume-ok.members[]`, NOT replayed from
 * the session buffer — `persist: false` on the server side, same as
 * `member.name-changed`).
 */
export const memberCursorChangedSchema = z.object({
    t: z.literal('member.cursor-changed'),
    uid: z.string(),
    cursor: customCursorSchema.nullable(),
    seq: z.number().int().nonnegative(),
    ts: z.number().int().nonnegative(),
});
/**
 * `member.cursor-draft` — c→s. Live in-progress editor state (full state,
 * not a diff), sent while the cursor editor is open. `cursor: null` means
 * "editing ended / cancelled". NOT persisted as member state — see
 * `member.cursor-drafted` below.
 */
export const memberCursorDraftSchema = z.object({
    t: z.literal('member.cursor-draft'),
    cursor: customCursorSchema.nullable(),
});
/**
 * `member.cursor-drafted` — s→c broadcast to everyone EXCEPT the originator.
 *
 * **Fully transient** (custom-cursor-2026-09-19.md §2.3): unlike every other
 * broadcast in this file, this one carries NO server stamps (`seq` / `ts`)
 * and is never appended to the session buffer or replayed on
 * welcome/resume — a client that misses one simply doesn't see that
 * in-progress edit until the next draft (or the eventual `cursor-changed`)
 * arrives. `room.welcome` deliberately does NOT include draft state (§2.3:
 * "draft は welcome に含めない" — a late joiner just waits up to one draft
 * interval to see it, keeping server state simpler).
 */
export const memberCursorDraftedSchema = z.object({
    t: z.literal('member.cursor-drafted'),
    uid: z.string(),
    cursor: customCursorSchema.nullable(),
});
//# sourceMappingURL=member.js.map